import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, Plus, X, Download, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminBlueprintPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [blueprints, setBlueprints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [audits, setAudits] = useState([]);
  
  // Gap Analysis data (if coming from gap analysis)
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [gapAnalyses, setGapAnalyses] = useState([]);
  const [selectedGapAnalysisId, setSelectedGapAnalysisId] = useState('');
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [primaryLocation, setPrimaryLocation] = useState('');
  const [services, setServices] = useState(['']);
  const [suburbs, setSuburbs] = useState(['']);
  const [selectedAuditId, setSelectedAuditId] = useState('');
  const [clientType, setClientType] = useState('blueprint_only');
  
  // Current presence
  const [hasWebsite, setHasWebsite] = useState(false);
  const [hasGbp, setHasGbp] = useState(false);
  const [hasSocialMedia, setHasSocialMedia] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  
  // Check for stored auth on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('blueprint_admin_auth');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  
  // Check for gap analysis data from URL or sessionStorage
  useEffect(() => {
    const fetchGapAnalysisData = async (id) => {
      try {
        const res = await fetch(`${API_URL}/api/gap-analysis/${id}`);
        if (res.ok) {
          const data = await res.json();
          setGapAnalysis(data);
          // Prefill form from gap analysis
          setBusinessName(data.business_name || '');
          setPrimaryLocation(data.primary_location || '');
          setServices(data.services?.length ? data.services : ['']);
          setSelectedAuditId(data.audit_id || '');
          setSelectedGapAnalysisId(data.id || '');
          setShowForm(true);
        }
      } catch (err) {
        console.error('Error fetching gap analysis:', err);
      }
    };

    if (isAuthenticated) {
      const fromGap = searchParams.get('from_gap');
      if (fromGap) {
        // Try to get gap analysis from sessionStorage first
        const storedGap = sessionStorage.getItem('gap_analysis_for_blueprint');
        if (storedGap) {
          const gapData = JSON.parse(storedGap);
          setGapAnalysis(gapData);
          // Prefill form
          setBusinessName(gapData.business_name || '');
          setPrimaryLocation(gapData.primary_location || '');
          setServices(gapData.services?.length ? gapData.services : ['']);
          setSelectedAuditId(gapData.audit_id || '');
          setSelectedGapAnalysisId(gapData.id || '');
          setShowForm(true);
          // Clear it after use
          sessionStorage.removeItem('gap_analysis_for_blueprint');
        } else {
          // Fetch from API
          fetchGapAnalysisData(fromGap);
        }
      }
    }
  }, [isAuthenticated, searchParams]);
  
  const prefillFromGapAnalysis = (gap) => {
    setBusinessName(gap.business_name || '');
    setPrimaryLocation(gap.primary_location || '');
    setServices(gap.services?.length ? gap.services : ['']);
    setSelectedAuditId(gap.audit_id || '');
    setSelectedGapAnalysisId(gap.id || '');
  };
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchBlueprints();
      fetchAudits();
      fetchGapAnalyses();
    }
  }, [isAuthenticated]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (res.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('blueprint_admin_auth', 'true');
        setAuthError('');
      } else {
        setAuthError('Invalid password');
      }
    } catch (err) {
      setAuthError('Authentication failed');
    }
  };
  
  const fetchBlueprints = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blueprints`);
      if (res.ok) {
        const data = await res.json();
        setBlueprints(data);
      }
    } catch (err) {
      console.error('Error fetching blueprints:', err);
    }
  };
  
  const fetchAudits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/audits`);
      if (res.ok) {
        const data = await res.json();
        setAudits(data);
      }
    } catch (err) {
      console.error('Error fetching audits:', err);
    }
  };
  
  const fetchGapAnalyses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis`);
      if (res.ok) {
        const data = await res.json();
        setGapAnalyses(data);
      }
    } catch (err) {
      console.error('Error fetching gap analyses:', err);
    }
  };
  
  const handleGapAnalysisSelect = (gapId) => {
    setSelectedGapAnalysisId(gapId);
    if (gapId) {
      const gap = gapAnalyses.find(g => g.id === gapId);
      if (gap) {
        prefillFromGapAnalysis(gap);
        setGapAnalysis(gap);
      }
    }
  };
  
  const handleAddService = () => {
    setServices([...services, '']);
  };
  
  const handleRemoveService = (index) => {
    const newServices = services.filter((_, i) => i !== index);
    setServices(newServices.length ? newServices : ['']);
  };
  
  const handleServiceChange = (index, value) => {
    const newServices = [...services];
    newServices[index] = value;
    setServices(newServices);
  };
  
  const handleAddSuburb = () => {
    setSuburbs([...suburbs, '']);
  };
  
  const handleRemoveSuburb = (index) => {
    const newSuburbs = suburbs.filter((_, i) => i !== index);
    setSuburbs(newSuburbs.length ? newSuburbs : ['']);
  };
  
  const handleSuburbChange = (index, value) => {
    const newSuburbs = [...suburbs];
    newSuburbs[index] = value;
    setSuburbs(newSuburbs);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        client_name: clientName,
        client_email: clientEmail,
        business_name: businessName,
        services: services.filter(s => s.trim()),
        primary_location: primaryLocation,
        suburbs: suburbs.filter(s => s.trim()),
        audit_id: selectedAuditId || null,
        gap_analysis_id: selectedGapAnalysisId || null,
        client_type: clientType,
        current_presence: {
          has_website: hasWebsite,
          has_gbp: hasGbp,
          has_social_media: hasSocialMedia,
          review_count: reviewCount,
          avg_rating: avgRating
        }
      };
      
      const res = await fetch(`${API_URL}/api/blueprints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const newBlueprint = await res.json();
        setBlueprints([newBlueprint, ...blueprints]);
        setShowForm(false);
        resetForm();
      } else {
        const error = await res.json();
        alert('Error creating blueprint: ' + error.detail);
      }
    } catch (err) {
      console.error('Error creating blueprint:', err);
      alert('Error creating blueprint');
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setClientName('');
    setClientEmail('');
    setBusinessName('');
    setPrimaryLocation('');
    setServices(['']);
    setSuburbs(['']);
    setSelectedAuditId('');
    setSelectedGapAnalysisId('');
    setGapAnalysis(null);
    setClientType('blueprint_only');
    setHasWebsite(false);
    setHasGbp(false);
    setHasSocialMedia(false);
    setReviewCount(0);
    setAvgRating(0);
  };
  
  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a2540] flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#d4af37] rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-[#0a2540]" />
            </div>
            <CardTitle className="text-2xl text-[#0a2540]">Blueprint Admin</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Enter password to access the Blueprint Generator</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Admin Password</Label>
                <Input 
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="mt-1"
                  data-testid="admin-password-input"
                />
              </div>
              {authError && (
                <p className="text-red-500 text-sm">{authError}</p>
              )}
              <Button 
                type="submit" 
                className="w-full bg-[#0a2540] text-white hover:bg-[#162536]"
                data-testid="admin-login-btn"
              >
                Access Blueprint Generator
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0a2540]">Blueprint Generator</h1>
            <p className="text-slate-600 mt-1">Create 12-month AI Visibility Blueprints for clients</p>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f]"
            data-testid="new-blueprint-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Blueprint
          </Button>
        </div>
        
        {showForm && (
          <Card className="mb-8 border-[#d4af37]" data-testid="blueprint-form">
            <CardHeader>
              <CardTitle>Create New Blueprint</CardTitle>
              {gapAnalysis && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ Pre-filled from Gap Analysis: {gapAnalysis.business_name}
                </p>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Gap Analysis Selector */}
                <div className="bg-slate-50 border rounded-lg p-4">
                  <Label htmlFor="gapAnalysisId" className="text-[#0a2540] font-semibold">Link to Gap Analysis (Recommended)</Label>
                  <select 
                    id="gapAnalysisId"
                    value={selectedGapAnalysisId}
                    onChange={(e) => handleGapAnalysisSelect(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                    data-testid="gap-analysis-select"
                  >
                    <option value="">-- Select a Gap Analysis --</option>
                    {gapAnalyses.map((gap) => (
                      <option key={gap.id} value={gap.id}>
                        {gap.business_name} - Score: {gap.overall_score}/100 ({gap.created_at?.slice(0,10)})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Linking a Gap Analysis adds "Where You Are Now" context to the Blueprint
                  </p>
                </div>
                
                {/* Where You Are Now Preview - Shows when Gap Analysis is selected */}
                {gapAnalysis && (
                  <div className="bg-gradient-to-r from-red-50 to-yellow-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-semibold text-[#0a2540] mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      Where You Are Now (From Gap Analysis)
                    </h3>
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className={`text-2xl font-bold ${gapAnalysis.overall_status === 'red' ? 'text-red-500' : gapAnalysis.overall_status === 'yellow' ? 'text-yellow-500' : 'text-green-500'}`}>
                          {gapAnalysis.overall_score}
                        </p>
                        <p className="text-xs text-slate-500">Overall Score</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-2xl font-bold text-red-500">{gapAnalysis.critical_findings}</p>
                        <p className="text-xs text-slate-500">Critical Issues</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-2xl font-bold text-yellow-500">{gapAnalysis.warning_findings}</p>
                        <p className="text-xs text-slate-500">Warnings</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-2xl font-bold text-slate-700">{gapAnalysis.total_findings}</p>
                        <p className="text-xs text-slate-500">Total Findings</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-7 gap-2">
                      {gapAnalysis.category_scores?.map((cat) => (
                        <div key={cat.category} className="text-center">
                          <div className={`h-2 rounded-full ${cat.status === 'red' ? 'bg-red-500' : cat.status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                          <p className="text-xs text-slate-500 mt-1 truncate" title={cat.category_name}>{cat.score}%</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-3 italic">
                      This data will be included in the Blueprint's "Where You Are Now" section
                    </p>
                  </div>
                )}
              
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="clientName">Client Name</Label>
                    <Input 
                      id="clientName"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientEmail">Client Email</Label>
                    <Input 
                      id="clientEmail"
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input 
                      id="businessName"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      required
                      data-testid="business-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="primaryLocation">Primary Location</Label>
                    <Input 
                      id="primaryLocation"
                      value={primaryLocation}
                      onChange={(e) => setPrimaryLocation(e.target.value)}
                      placeholder="e.g., Charlotte, NC"
                      required
                      data-testid="location-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Services</Label>
                  <div className="space-y-2">
                    {services.map((service, index) => (
                      <div key={index} className="flex gap-2">
                        <Input 
                          value={service}
                          onChange={(e) => handleServiceChange(index, e.target.value)}
                          placeholder="e.g., Roofing"
                          data-testid={`service-input-${index}`}
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleRemoveService(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={handleAddService}>
                      <Plus className="w-4 h-4 mr-1" /> Add Service
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label>Service Areas (Suburbs)</Label>
                  <div className="space-y-2">
                    {suburbs.map((suburb, index) => (
                      <div key={index} className="flex gap-2">
                        <Input 
                          value={suburb}
                          onChange={(e) => handleSuburbChange(index, e.target.value)}
                          placeholder="e.g., Huntersville"
                          data-testid={`suburb-input-${index}`}
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleRemoveSuburb(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={handleAddSuburb}>
                      <Plus className="w-4 h-4 mr-1" /> Add Area
                    </Button>
                  </div>
                </div>
                
                <div>
                    <Label htmlFor="auditId">Link to Audit (Optional)</Label>
                    <select 
                      id="auditId"
                      value={selectedAuditId}
                      onChange={(e) => setSelectedAuditId(e.target.value)}
                      className="w-full border rounded-md p-2"
                      data-testid="audit-select"
                    >
                      <option value="">-- Select an audit --</option>
                      {audits.map((audit) => (
                        <option key={audit.id} value={audit.id}>
                          {audit.business_name} - {audit.visibility_score}% ({audit.timestamp?.slice(0,10)})
                        </option>
                      ))}
                    </select>
                  </div>
                
                <div>
                  <Label htmlFor="clientType">Client Type</Label>
                  <select 
                    id="clientType"
                    value={clientType}
                    onChange={(e) => setClientType(e.target.value)}
                    className="w-full border rounded-md p-2"
                    data-testid="client-type-select"
                  >
                    <option value="blueprint_only">Blueprint Only ($997)</option>
                    <option value="dfy">Done-For-You (DFY)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {clientType === 'blueprint_only' 
                      ? 'Client sees read-only dashboard during strategy call' 
                      : 'Client gets full interactive dashboard with progress tracking'}
                  </p>
                </div>
                
                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label className="text-lg font-semibold mb-4 block">Current Presence</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={hasWebsite} 
                        onChange={(e) => setHasWebsite(e.target.checked)}
                      />
                      Has Website
                    </label>
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={hasGbp} 
                        onChange={(e) => setHasGbp(e.target.checked)}
                      />
                      Has Google Business Profile
                    </label>
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={hasSocialMedia} 
                        onChange={(e) => setHasSocialMedia(e.target.checked)}
                      />
                      Has Social Media
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Current Review Count</Label>
                      <Input 
                        type="number" 
                        value={reviewCount}
                        onChange={(e) => setReviewCount(parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Average Rating</Label>
                      <Input 
                        type="number" 
                        step="0.1"
                        min="0"
                        max="5"
                        value={avgRating}
                        onChange={(e) => setAvgRating(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-[#0a2540] text-white hover:bg-[#0a2540]/90"
                    data-testid="create-blueprint-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Blueprint'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        
        <div className="grid gap-4">
          <h2 className="text-xl font-semibold text-[#0a2540]">Existing Blueprints</h2>
          {blueprints.length === 0 ? (
            <Card className="p-8 text-center text-slate-500">
              No blueprints created yet. Click "New Blueprint" to get started.
            </Card>
          ) : (
            blueprints.map((blueprint) => (
              <Card key={blueprint.id} className="hover:shadow-md transition-shadow" data-testid={`blueprint-${blueprint.id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg text-[#0a2540]">{blueprint.business_name}</h3>
                      <p className="text-slate-600">{blueprint.client_name} • {blueprint.client_email}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {blueprint.primary_location} • {blueprint.services?.join(', ')}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Created: {blueprint.created_at?.slice(0, 10)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`/blueprint/${blueprint.id}`, '_blank')}
                        data-testid={`view-blueprint-${blueprint.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View Dashboard
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/blueprints/${blueprint.id}/pdf`);
                            const data = await res.json();
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(data.html);
                            printWindow.document.close();
                            printWindow.onload = () => {
                              printWindow.print();
                            };
                          } catch (err) {
                            console.error('Error generating PDF:', err);
                            alert('Error generating PDF');
                          }
                        }}
                        data-testid={`download-pdf-${blueprint.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-4 text-sm">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {blueprint.tasks?.length || 0} Tasks
                    </span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                      {blueprint.tasks?.filter(t => t.completed)?.length || 0} Completed
                    </span>
                    {blueprint.client_type === 'dfy' ? (
                      <span className="bg-[#d4af37]/20 text-[#0a2540] px-2 py-1 rounded font-medium">
                        DFY Client
                      </span>
                    ) : (
                      <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded">
                        Blueprint Only
                      </span>
                    )}
                    {blueprint.audit_id && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        Linked to Audit
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBlueprintPage;
